/**
 * Product Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly coupled to the Product template.
 *
 * @namespace product
 */
import {formatMoney} from '@shopify/theme-currency';
import {getUrlWithVariant, ProductForm} from '@shopify/theme-product-form';
import {register} from '@shopify/theme-sections';
import Flickity from 'flickity';

import 'flickity-imagesloaded';
import 'flickity-fullscreen';
import 'flickity-as-nav-for';
import qtySelector from '../components/qty-selector';
import modelViewerLoader from '../components/model-viewer-loader';
import {playPause} from '../utils/play-pause';
import {fetchItems, addToWishList, removeFromWishList} from '../utils/wishlist';
import {klaviyoTrackViewedProduct} from '../components/klaviyo-tracking-events';
import initShareToggle from '../components/social-share';
import initTrustIndicators from '../components/trust-indicators';
import initPaymentDrawerToggle from '../components/payment-drawer';
import initProductNotifyMe from '../components/product-notify-me';

const selectors = {
  addToCart: '[data-add-to-cart]',
  comparePrice: '[data-product-compare-at-price]',
  priceWrapper: '[data-price-wrapper]',
  productJson: '[data-product-json]',
  modelsJson: '[data-product-models-json]',
  productImageWrapper: '[data-product-image-wrapper]',
  productVideoWrapper: '[data-product-video-wrapper]',
  productPrice: '[data-product-price]',
  storeAvailabilityContainer: '[data-store-availability-container]',
  decrementQtyBtn: '[data-decrement-qty]',
  incrementQtyBtn: '[data-increment-qty]',
  qtyInput: '[data-qty-input]',
  addToWishlist: '[data-wishlist-add]',
};

const cssClasses = {
  hide: 'hide',
  onsale: 'product__price--on-sale',
  hidden: 'hidden',
  isVideo: 'is-video',
};

/**
 * Product section constructor. Runs on page load as well as Theme Editor
 * `section:load` events.
 * @param {string} container - selector for the section container DOM element
 */

export default register('product', {
  onLoad() {
    // Register the product form using @shopify/theme-product-form
    const formElement = this.container.querySelector('[data-product-form]');
    const productHandle = formElement.dataset.productHandle;
    const options = {
      onOptionChange: this.onOptionChange.bind(this),
      onFormSubmit: this.onFormSubmit.bind(this),
      // theme-product-form requires callbacks to be passed
      // for all four form events.
      onQuantityChange: () => true,
      onPropertyChange: () => true,
    };
    fetch(`/products/${productHandle}.js`)
      .then((response) => response.json())
      .then((productJSON) => {
        this.productForm = new ProductForm(formElement, productJSON, options);
        const addToCartBtn = this.container.querySelector(selectors.addToCart);
        addToCartBtn?.classList.remove(cssClasses.hidden);
      })
      .catch((err) => console.log(err)); // eslint-disable-line no-console

    // Parse the settings string passed through from liquid
    const settingsData = this.container.dataset.sectionSettings || '{}';
    this.sectionSettings = JSON.parse(settingsData);

    const data = this.container.querySelector(selectors.productJson)?.innerHTML || '{}';
    this.productData = JSON.parse(data);

    setTimeout(() => {
      klaviyoTrackViewedProduct(this.productData);
    }, 3000);

    // If URL contains ?variant= param, get the selected variant ID
    const selectedVariant = this.container.dataset.selectedVariant;

    if (this.sectionSettings.display_pickup_availabilities) {
      this.storeAvailability = new theme.StoreAvailability(
        this.container.querySelector(selectors.storeAvailabilityContainer),
      );
      this.storeAvailability.updateContent(selectedVariant);
    }

    // Look for a product image slideshow.
    this.slideShow = this.container.querySelector('.product__slideshow');

    // Initialise the slider
    this.slider = null;
    if (this.slideShow) {
      const container = this.container;
      const slideshow = this.slideShow;
      const settings = this.sectionSettings;
      const updateFeaturedImage = this.updateFeaturedImage.bind(this);
      this.slider = new Flickity(slideshow, {
        adaptiveHeight: true,
        draggable: '>1',
        fullscreen: this.sectionSettings.enable_zoom,
        pageDots: settings.include_pager_dots,
        prevNextButtons: this.sectionSettings.show_arrows,
        watchCSS: true,
        wrapAround: settings.infinite,
        on: {
          ready() {
            // If enabled, connect slider previous/next buttons
            this.previousButton = container.querySelector('.controls-container--main .btn--control-prev');
            this.nextButton = container.querySelector('.controls-container--main .btn--control-next');

            this?.previousButton?.addEventListener('click', () => this.previous(settings.infinite, false));
            this?.nextButton?.addEventListener('click', () => this.next(settings.infinite, false));

            const modelViewers = this.slider.querySelectorAll('model-viewer');
            if (modelViewers.length > 0) {
              const modelViewerVersion = modelViewers[0].dataset.version;
              const modelsData = container.querySelector(selectors.modelsJson)?.textContent;
              if (modelViewerVersion && modelsData) {
                const modelsDataJson = JSON.parse(modelsData);
                modelViewerLoader(modelViewerVersion, modelsDataJson)
                  .then(() => null)
                  .catch(() => null);
              }

              // Stop slider drag events from impacting 3D model interaction
              const modelStopPropagation = (event) => event.stopPropagation();
              modelViewers.forEach((modelViewer) => {
                modelViewer.addEventListener('mousedown', modelStopPropagation);
                modelViewer.addEventListener('touchstart', modelStopPropagation);
                modelViewer.addEventListener('pointerdown', modelStopPropagation);
              });
            }

            // Select the slider image matching the current variant
            updateFeaturedImage(selectedVariant ? selectedVariant : null, this);
          },

          change(index) {
            slideshow.classList.remove(cssClasses.isVideo);

            const videoMediaWrappers = this.slider.querySelectorAll(selectors.productVideoWrapper);
            videoMediaWrappers.forEach((wrapper) => {
              // Pause all videos on slide change
              if (wrapper.dataset.mediaType === 'video') {
                const video = wrapper.querySelector('video');
                video.pause();
              } else {
                const iframe = wrapper.querySelector('iframe');
                playPause(iframe, 'pause');
              }

              // If current video is the current slide
              const slideIndex = parseInt(wrapper.dataset.slideIndex, 10);
              if (index === slideIndex) {
                slideshow.classList.add(cssClasses.isVideo);

                // Play current video if autoplay enabled
                if (settings.video_controls === 'autoplay') {
                  if (wrapper.dataset.mediaType === 'video') {
                    const video = wrapper.querySelector('video');
                    video.play();
                  } else {
                    const iframe = wrapper.querySelector('iframe');
                    playPause(iframe, 'play');
                  }
                }
              }
            });
          },
        },
      });
    }

    // Look for a product thumbnail slideshow.
    this.thumbnailSlideshow = this.container.querySelector('.product__thumbnails');

    // Initialise the thumbnail slider
    this.thumbSlider = null;
    if (this.thumbnailSlideshow) {
      const container = this.container;
      const thumbSlider = container.querySelector('.product__thumbnails');
      this.thumbSlider = new Flickity(thumbSlider, {
        freeScroll: true,
        prevNextButtons: false,
        autoPlay: false,
        pageDots: false,
        imagesLoaded: true,
        setGallerySize: true,
        watchCSS: false,
        asNavFor: this.slideShow,
        on: {
          ready() {
            // If enabled, connect thumbnail slider previous/next buttons
            this.previousThumbButton = container.querySelector('.controls-container--thumbs .btn--control-prev');
            this.nextThumbButton = container.querySelector('.controls-container--thumbs .btn--control-next');

            this?.previousThumbButton?.addEventListener('click', () => this.previous());
            this?.nextThumbButton?.addEventListener('click', () => this.next());
          },
        },
      });
    }

    qtySelector(
      this.container.querySelector(selectors.decrementQtyBtn),
      this.container.querySelector(selectors.incrementQtyBtn),
      this.container.querySelector(selectors.qtyInput),
    );

    // Handle wishlist add / remove
    this.addToWishlistBtn = this.container.querySelector(selectors.addToWishlist);
    this.isWishlisted = false;
    this.wishlistProduct = {};

    if (selectedVariant) {
      this.setWishlistVariant(Number(selectedVariant));
    }

    if (this.addToWishlistBtn) {
      this.addToWishlistBtn.addEventListener('click', () => {
        if (this.isWishlisted) {
          removeFromWishList(this.wishlistProduct);
        } else {
          addToWishList(this.wishlistProduct);
        }

        this.isWishlisted = !this.isWishlisted;

        this.updateWishlistBtnState();
      });
    }

    // Initialise the payment options drawer toggle
    initPaymentDrawerToggle();

    // Initialise notify me modal
    initProductNotifyMe(this.container);

    // Initialise the trust indicators slider
    initTrustIndicators(this.container);

    // Initialise the awards sliders
    const awardsSliders = this.container.querySelectorAll('.product__awards');
    awardsSliders.forEach((awardsSlider) => {
      // eslint-disable-next-line no-unused-vars
      const flickity = new Flickity(awardsSlider, {
        freeScroll: true,
        prevNextButtons: false,
        pageDots: false,
        cellAlign: 'left',
        imagesLoaded: true,
        contain: true,
        autoPlay: this.sectionSettings && this.sectionSettings.autoplay,
      });
    });

    // Initialise the social share toggle
    initShareToggle();
  },

  updateWishlistBtnState() {
    if (this.isWishlisted) {
      this.addToWishlistBtn.classList.add('is-wishlisted');
      this.addToWishlistBtn.setAttribute('aria-label', window.theme.strings.wishlist.remove);
    } else {
      this.addToWishlistBtn.classList.remove('is-wishlisted');
      this.addToWishlistBtn.setAttribute('aria-label', window.theme.strings.wishlist.add);
    }
  },

  setWishlistVariant(currentVariantId) {
    this.wishlistProduct = {
      epi: currentVariantId,
      du: `${window.location.origin}/products/${this.productData.handle}`,
      empi: this.productData.id,
      pr: this.productData.price,
    };

    fetchItems()
      .then((currentWishlistItems) => {
        this.isWishlisted = currentWishlistItems.some((item) => Number(item.epi) === Number(currentVariantId));

        if (this.addToWishlistBtn) {
          this.updateWishlistBtnState();
        }

        return true;
      })
      .catch((error) => {
        window.console.log(error);
      });
  },

  // Event callback for option/variant switch event
  onOptionChange(event) {
    const variant = event.dataset.variant;

    /* eslint-disable no-negated-condition */
    if (!variant) {
      // The combination of selected options does not have a matching variant
      this.updateAddToCartState(null);
      this.updateProductPrices(false);
      this.updateFeaturedImage(false);
    } else if (!variant.available) {
      // The combination of selected options has a matching variant but it is
      // currently unavailable
      this.updateAddToCartState(false);
      this.updateProductPrices(true, variant.price, variant.compare_at_price);
      this.updateFeaturedImage(variant.id);
      const url = getUrlWithVariant(window.location.href, variant.id);
      window.history.replaceState({path: url}, '', url);
    } else {
      // The combination of selected options has a matching variant and it is
      // available
      this.updateAddToCartState(true);
      this.updateProductPrices(true, variant.price, variant.compare_at_price);
      this.updateFeaturedImage(variant.id);
      const url = getUrlWithVariant(window.location.href, variant.id);
      window.history.replaceState({path: url}, '', url);
      if (this.sectionSettings.display_pickup_availabilities) {
        this.storeAvailability.updateContent(variant.id);
      }
    }
    /* eslint-enable no-negated-condition */
  },

  // Event callback for Theme Editor `section:unload` event
  onUnload() {
    this.productForm.destroy();
  },

  // Event callback for product form submit event
  onFormSubmit(event) {
    event.preventDefault();
    const variant = event.dataset.variant;
    const quantity = event.dataset.quantity;
    const properties = event.dataset.properties ?? {};
    if (variant && variant.id && quantity) {
      const item = {
        id: variant.id,
        quantity,
        properties,
      };
      window.sideCart.handleAddToCartEvent(item, true);
    }
  },

  /**
   * Updates the DOM state of the add to cart button
   *
   * @param {boolean} enabledState - True is enabled. False is disabled. Null is disabled without prices.
   * @param {string} priceWrapper - DOM selector for prices container div
   * @param {string} button - DOM selector for add to cart button
   */
  updateAddToCartState(enabledState) {
    const addToCartBtn = this.container.querySelector(selectors.addToCart);
    const priceWrapper = this.container.querySelector(selectors.priceWrapper);

    switch (enabledState) {
      // Variant is available
      case true: {
        priceWrapper.classList.remove(cssClasses.hide);
        addToCartBtn.removeAttribute('disabled');
        addToCartBtn.textContent = theme.strings.addToCart;
        break;
      }
      // Variant is unavailable
      case false: {
        priceWrapper.classList.remove(cssClasses.hide);
        addToCartBtn.setAttribute('disabled', '');
        addToCartBtn.textContent = theme.strings.soldOut;
        break;
      }
      // No matching variant
      case null: {
        priceWrapper.classList.add(cssClasses.hide);
        addToCartBtn.setAttribute('disabled', '');
        addToCartBtn.textContent = theme.strings.unavailable;
        break;
      }
    }
  },

  /**
   * Updates the DOM with specified prices
   *
   * @param {boolean} variantExists - Selected options match a valid variant
   * @param {number} price - Selected variant price
   * @param {number} compareAtPrice - Selected variant original price
   * @param {string} productPrice - DOM selector for current price of the product
   * @param {string} comparePrice - DOM selector for original price of the product
   * @param {string} priceWrapper - DOM selector for prices container div
   */
  updateProductPrices(variantExists, price, compareAtPrice) {
    const comparePrice = this.container.querySelector(selectors.comparePrice);
    const productPrice = this.container.querySelector(selectors.productPrice);
    const priceWrapper = this.container.querySelector(selectors.priceWrapper);

    // No matching variant
    if (!variantExists) {
      comparePrice.textContent = '';
      comparePrice.classList.add(cssClasses.hide);
      priceWrapper.classList.remove(cssClasses.onsale);
      return;
    }

    // Variant exists
    productPrice.textContent = formatMoney(price, theme.moneyFormat);

    if (price < compareAtPrice) {
      comparePrice.textContent = formatMoney(compareAtPrice, theme.moneyFormat);
      priceWrapper.classList.add(cssClasses.onsale);
      comparePrice.classList.remove(cssClasses.hide);
    } else {
      comparePrice.textContent = '';
      priceWrapper.classList.remove(cssClasses.onsale);
      comparePrice.classList.add(cssClasses.hide);
    }
  },

  /**
   * Scrolls the image slider to that of the current variant
   *
   * @param {number} variantId - ID of the selected variant
   * @param {string} sliderImages - DOM selector for product image slider
   */
  updateFeaturedImage(variantId, thisSlider) {
    const id = variantId ? variantId : this.container.dataset.firstVariant;
    const slider = thisSlider ? thisSlider : this.slider;

    const sliderImages = this.container.querySelectorAll(selectors.productImageWrapper);
    if (sliderImages.length > 0) {
      for (let i = 0; i < sliderImages.length; i++) {
        const slide = sliderImages[i];
        const isVariantImage = slide.dataset.imageVariants.includes(id);

        if (isVariantImage && slider) {
          slider.select(Number(slide.dataset.slideIndex), this.sectionSettings.infinite, false);
          break;
        }
      }
    }
  },
});

theme.StoreAvailability = (function () {
  const Selectors = {
    storeAvailabilityMore: '[store-availability-more]',
    storeAvailabilityMoreOpenTrigger: '[data-store-availability-more-open]',
    storeAvailabilityMoreProductTitle: '[data-store-availability-more-product-title]',
    storeAvailabilityMoreVariantTitle: '[data-store-availability-more-variant-title]',
  };

  const classes = {
    hidden: 'hide',
  };

  function StoreAvailability(container) {
    this.container = container;
    this.productTitle = this.container.dataset.productTitle;
    this.hasOnlyDefaultVariant = this.container.dataset.hasOnlyDefaultVariant === 'true';
  }
  StoreAvailability.prototype = {
    ...StoreAvailability.prototype,
    updateContent(variantId) {
      const variantSectionUrl = `${this.container.dataset.baseUrl}variants/${variantId}/?section_id=store-availability`;
      const self = this;

      let storeAvailabilityMoreOpenTrigger = document.querySelector(Selectors.storeAvailabilityMoreOpenTrigger);

      this.container.style.opacity = 0.5;
      if (storeAvailabilityMoreOpenTrigger) {
        storeAvailabilityMoreOpenTrigger.disabled = true;
        storeAvailabilityMoreOpenTrigger.setAttribute('aria-busy', true);
      }

      async function fetchStoreAvailabilities(url) {
        const storeAvailabilityHTMLResponse = await fetch(url);
        const storeAvailabilityHTML = await storeAvailabilityHTMLResponse.text();
        if (storeAvailabilityHTML.trim() === '') {
          return;
        }
        self.container.innerHTML = storeAvailabilityHTML;
        self.container.innerHTML = self.container.firstElementChild.innerHTML;
        self.container.style.opacity = 1;

        // Need to query this again because we updated the DOM
        storeAvailabilityMoreOpenTrigger = document.querySelector(Selectors.storeAvailabilityMoreOpenTrigger);
        storeAvailabilityMoreOpenTrigger?.addEventListener('click', () => self._onClickDisplayMorePickupOptions());

        self._updateProductTitle();
        if (self.hasOnlyDefaultVariant) {
          self._hideVariantTitle();
        }
      }

      fetchStoreAvailabilities(variantSectionUrl);
    },

    clearContent() {
      this.container.innerHTML = '';
    },

    _onClickDisplayMorePickupOptions() {
      const storeAvailabilityMore = this.container.querySelector(Selectors.storeAvailabilityMore);
      if (storeAvailabilityMore && storeAvailabilityMore.classList.contains('store-availabilities-more--active')) {
        storeAvailabilityMore.classList.remove('store-availabilities-more--active');
      } else {
        storeAvailabilityMore.classList.add('store-availabilities-more--active');
      }
    },

    _updateProductTitle() {
      const storeAvailabilityMoreProductTitle = this.container.querySelector(
        Selectors.storeAvailabilityModalProductTitle,
      );
      if (storeAvailabilityMoreProductTitle) storeAvailabilityMoreProductTitle.textContent = this.productTitle;
    },

    _hideVariantTitle() {
      const storeAvailabilityMoreVariantTitle = this.container.querySelector(
        Selectors.storeAvailabilityMoreVariantTitle,
      );
      if (storeAvailabilityMoreVariantTitle) storeAvailabilityMoreVariantTitle.classList.add(classes.hidden);
    },
  };

  return StoreAvailability;
})();
